import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiProvider";
import { queryKeys, useHealthMatrix, useProjects } from "../../api/queries";
import type { HealthCell } from "../../api/types";

/**
 * One row of the Health page (ADR-0030 P4-2): a health cell plus, when
 * resolvable, the project id its service lives under — needed to build the
 * `/projects/:projectId/services/:serviceId` link, since `HealthCell` itself
 * only carries `serviceId` (mirrors `HealthCellJSON` exactly, see types.ts).
 * `projectId` is `undefined` when it couldn't be resolved (the fan-out below
 * degraded, or the service moved/vanished in a race) — the page falls back
 * to plain text for that cell's service name rather than a broken link.
 */
export interface HealthRow extends HealthCell {
  projectId: string | undefined;
}

export interface HealthRowsResult {
  rows: HealthRow[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /**
   * True when the health matrix itself loaded fine but resolving the
   * service->project index (for linking) failed — cells still render with
   * an unlinked service name rather than failing the whole page, mirroring
   * `useRouteRows`'s `placementsDegraded` posture for a secondary,
   * non-blocking resolution failure.
   */
  linksDegraded: boolean;
  refetch: () => void;
}

/** Capped fan-out, matching `useRouteRows`'s documented pre-alpha N+1: one
 * page of projects, one page of services per project (`limit: 100` each). */
const FAN_OUT_LIMIT = 100;

/** Fetches the fleet-wide health matrix and, alongside it, fans out the
 * fleet's projects/services just far enough to resolve each cell's
 * `serviceId` back to the project id its detail link needs. */
export function useHealthRows(): HealthRowsResult {
  const client = useApiClient();

  const {
    data: healthPage,
    isPending: healthPending,
    isError: healthIsError,
    error: healthError,
    refetch: refetchHealth,
  } = useHealthMatrix({ limit: 200 });

  const {
    data: projectsPage,
    isPending: projectsPending,
    isError: projectsIsError,
    refetch: refetchProjects,
  } = useProjects({ limit: FAN_OUT_LIMIT });
  const projects = useMemo(() => projectsPage?.items ?? [], [projectsPage]);

  const serviceQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: queryKeys.services(project.id),
      queryFn: () =>
        client.listServices({ projectId: project.id, limit: FAN_OUT_LIMIT }),
      enabled: Boolean(project.id),
    })),
  });
  const servicesIsError = serviceQueries.some((q) => q.isError);
  const servicesPending = serviceQueries.some((q) => q.isPending);

  const serviceProjectIndex = useMemo(() => {
    const index = new Map<string, string>();
    projects.forEach((project, i) => {
      const services = serviceQueries[i]?.data?.items ?? [];
      services.forEach((service) => index.set(service.id, project.id));
    });
    return index;
  }, [projects, serviceQueries]);

  const rows: HealthRow[] = useMemo(
    () =>
      (healthPage?.items ?? []).map((cell) => ({
        ...cell,
        projectId: serviceProjectIndex.get(cell.serviceId),
      })),
    [healthPage, serviceProjectIndex],
  );

  // Page loading hinges on the health matrix itself, not the link fan-out —
  // an empty (or slow/disabled) fan-out must never trap the page in loading.
  // This matters in particular for `enabled: false` queries (no projects to
  // fan out over): React Query v5 reports those as `isPending: true`
  // forever, which previously kept an empty fleet stuck on the loading
  // spinner instead of falling through to the empty state. But when the
  // matrix DOES have cells to show, we still wait for the fan-out so rows
  // render already-linked rather than flashing unlinked text first — there's
  // nothing to wait for when there's nothing to link.
  const hasRows = (healthPage?.items.length ?? 0) > 0;
  const isPending = healthPending || (hasRows && (projectsPending || servicesPending));

  return {
    rows,
    isPending,
    isError: healthIsError,
    error: healthError,
    linksDegraded: projectsIsError || servicesIsError,
    refetch: () => {
      void refetchHealth();
      void refetchProjects();
      serviceQueries.forEach((q) => void q.refetch());
    },
  };
}
